import { Link } from "@/components/site/LocaleLink";
import { ArrowRight } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Picture } from "@/components/site/Picture";
import { Reveal } from "@/components/site/Reveal";
import { useReducedMotion } from "@/hooks/useReducedMotion";

/**
 * Full-bleed photographic band used to break up the stacked content sections on
 * the home page — a large image, a dark scrim, and a short headline with one
 * call to action.
 *
 * `align` places the text block at the start or the centre of the band. Start
 * alignment follows the reading direction, so it sits left in English and right
 * in Arabic without any extra handling.
 */
export const PhotoBand = ({
  img,
  alt = "",
  kicker,
  title,
  sub,
  cta,
  to,
  align = "start",
  testId,
}) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  const centered = align === "center";

  return (
    <section
      ref={ref}
      className="relative flex min-h-[26rem] items-center overflow-hidden py-24 lg:min-h-[34rem] lg:py-32"
      data-testid={testId}
    >
      <motion.div style={reduced ? {} : { y }} className="absolute inset-0 -z-10 scale-110">
        <Picture img={img} alt={alt} className="h-full w-full object-cover" sizes="100vw" />
      </motion.div>

      {/* Two scrims: a vertical one for overall legibility, and a directional
          one so start-aligned copy keeps contrast over a busy photograph. */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-forest-deep/90 via-forest-deep/60 to-forest-deep/40" />
      {!centered && <div className="scrim-start absolute inset-0 -z-10" />}
      <div className="noise pointer-events-none absolute inset-0 -z-10 opacity-[0.05]" />

      <div className="container-x relative w-full">
        <div className={`max-w-2xl ${centered ? "mx-auto text-center" : ""}`}>
          {kicker && (
            <Reveal>
              <p className="eyebrow mb-4 text-gold-light">{kicker}</p>
            </Reveal>
          )}
          <Reveal i={1}>
            <h2 className="font-display text-3xl font-extrabold leading-tight tracking-tight text-cream sm:text-4xl lg:text-5xl">
              {title}
            </h2>
          </Reveal>
          {sub && (
            <Reveal i={2}>
              <p className="mt-5 text-base leading-relaxed text-cream/85 sm:text-lg">{sub}</p>
            </Reveal>
          )}
          {cta && to && (
            <Reveal i={3}>
              <Link
                to={to}
                data-testid={testId ? `${testId}-cta` : undefined}
                className={`group mt-9 inline-flex items-center gap-2 rounded-full bg-cream px-7 py-4 text-sm font-semibold text-forest-deep transition-all duration-300 hover:-translate-y-1 hover:bg-white ${
                  centered ? "mx-auto" : ""
                }`}
              >
                {cta}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 rtl-flip" />
              </Link>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};
